import type { TileSize } from '@pams/shared';
import { CONTENT_RUNGS } from './layout';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DASHBOARD GRID, IN CONTENT PIXELS. [P6a]
 *
 *  ⚠️ CONTAINER QUERIES ON `<main>`, NEVER `sm:`/`md:`/`lg:`. Tailwind's breakpoints measure
 *  the WINDOW, and `<main>` is 304px narrower than it with the sidebar out — `lg` (1024) fires
 *  when the content has 720. `AppShell`'s `<main>` carries `@container`, so `@min-[992px]:`
 *  IS the statement about the space the grid actually has, and it stays right when the
 *  sidebar collapses to its 56px rail, which no media query can be.
 *
 *  ⚠️ AND IT IS CSS-ONLY. `web/vitest.setup.ts` stubs `matchMedia` to `matches: false` for
 *  ever, so a width-BRANCHING render would take the narrow path in every unit test — silently
 *  deleting columns from dozens of assertions. A container query is inert in jsdom rather
 *  than wrong.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The narrowest a `sm` tile may be and still hold its number, its unit and its status word. */
export const TILE_MIN_PX = 320;
/** `gap-4`. Declared, so the thresholds below are arithmetic rather than taste. */
export const GRID_GAP_PX = 16;

const fits = (columns: number): number => columns * TILE_MIN_PX + (columns - 1) * GRID_GAP_PX;

export const DASHBOARD_COLUMN_PX = { two: fits(2), three: fits(3), four: fits(4) } as const;

/**
 * ⚠️ LITERAL CLASS STRINGS, NEVER `grid-cols-${n}`. Tailwind extracts candidates from source
 * TEXT and generates nothing for an interpolated class — so an interpolated grid renders as
 * one column at every width, with no error anywhere. It is the one way this file can ship
 * completely broken while `tsc`, `eslint` and `vitest` all stay green.
 *
 * ⚠️ AND NO `@max-` APPEARS. The complementarity gap [UX-FINAL] records — `@max-[919px]`
 * beside `@min-[920px]` leaving `919 ≤ w < 920` uncovered, on a container width that is
 * fractional — only exists where the two sit together. A pure ascending `@min-` cascade has
 * no uncovered interval at all, and `dashboardGrid.test.ts` keeps it that way.
 */
export const DASHBOARD_GRID =
  'grid grid-cols-1 gap-4 @min-[656px]:grid-cols-2 @min-[992px]:grid-cols-3 @min-[1328px]:grid-cols-4';

/**
 * ⚠️ FOUR IS A CAP, NOT A STRETCH. A fifth column FITS at 1920 content (`fits(5)` is 1664)
 * and is refused: seven tiles at four columns reads as 4+3, which is a grid; at five it is
 * 5+2, which is a broken row. It is a no-op at every rung below 1664, so it cannot regress
 * the 1366 floor — `CONTENT_MAX_PX`'s own argument one level down.
 */
/**
 * ⚠️ [P6b.2] ONE GRID PER ROW GROUP, WHICH IS WHAT MAKES A SHORT ROW STAY SHORT.
 *
 * `DASHBOARD_GRID` is a single flowing grid: a tile lands wherever the previous one left off,
 * so two small tiles an operator wanted side by side get a third pushed up beside them the
 * moment the widths allow. Rendering each row as its OWN grid ends the row where the operator
 * said it ends, full or not — and costs nothing else, because the column ladder is identical.
 *
 * ⚠️ THE CLASS STRING IS THE SAME ONE. It has to be: `TILE_SPAN` spans against this column
 * count, so a row grid with a different ladder would make `lg` mean two different things on
 * one page. The wrapper below is what stacks the rows, and it is `flex` rather than a grid so
 * a row's height is its own.
 */
export const DASHBOARD_ROW_GRID = DASHBOARD_GRID;

/** The column that holds the row grids. Gap matches the grid's, so rows and columns agree. */
export const DASHBOARD_ROW_STACK = 'flex flex-col gap-4';

export const TILE_SPAN: Record<TileSize, string> = {
  sm: 'col-span-1',
  md: 'col-span-1 @min-[656px]:col-span-2',
  lg: 'col-span-1 @min-[656px]:col-span-2 @min-[992px]:col-span-3 @min-[1328px]:col-span-4',
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1e §1a] THE ONE TILE WHOSE BODY SCROLLS, AND THE TWO RULES THAT GOVERN IT.
 *
 *  ⚠️ RULE ONE — THE CAP IS ARITHMETIC AND THE CLASS IS A LITERAL. `max-h-[${n}px]` generates
 *  NOTHING (Tailwind extracts candidates from source TEXT), so the tile would silently grow to
 *  fit its twenty rows with `tsc`, `eslint` and `vitest` all green — the same failure mode this
 *  file's own `grid-cols-${n}` note records. `dashboardGrid.test.ts` pins the literal against
 *  the arithmetic.
 *
 *  ⚠️ RULE TWO — `overscroll-behavior` STAYS AT ITS DEFAULT, AND THAT DEPARTS FROM THE HOUSE
 *  IDIOM ON PURPOSE. `AppSidebar` and the officer app's shell both use `overscroll-contain`,
 *  correctly: they are full-height fixed columns where chaining would scroll the page BEHIND
 *  navigation somebody is reading. `contain` prevents chaining AT THE BOUNDARY — which is
 *  exactly "trapping the wheel at either end", the one thing §1a forbids, because a tile's list
 *  sits in the page flow and reading it to the end is a natural continuation into the rest of
 *  the page. All three of §1a's requirements then fall out of the default:
 *
 *    • takes the wheel only when it has more content → a non-overflowing `overflow-y-auto`
 *      box is not a scroll container at all;
 *    • takes it only when the pointer is inside → the wheel targets the element under it;
 *    • does not trap at either end → chaining, which is what `auto` means.
 *
 *  ⚠️ NONE OF THIS IS VISIBLE TO jsdom, which performs no layout and dispatches no real wheel.
 *  `e2e/specs/dashboard.spec.ts` is what holds it, and its assertion must read `main.scrollTop`
 *  — `AppShell` is `h-screen overflow-hidden`, so the DOCUMENT never scrolls and an assertion
 *  on `documentElement` would pass whatever happened.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/** One row: `py-1.5` either side of a 20px line. */
export const TILE_ROW_PX = 32;
/** §1a: five or six rows visible, and the rest scrolled to. */
export const TILE_VISIBLE_ROWS = 6;
export const TILE_SCROLL_MAX_PX = TILE_ROW_PX * TILE_VISIBLE_ROWS;
/** ⚠️ A LITERAL. See rule one — and `dashboardGrid.test.ts` proves it matches the arithmetic. */
export const TILE_SCROLL = 'max-h-[192px] overflow-y-auto';

/** The floor rung, so the test can say what the client's own laptop gets. */
export const FLOOR_CONTENT_PX = CONTENT_RUNGS[0]?.content ?? 0;
